import type { FrameName } from "@/game/entities/config";
import type {
	AudioSpriteNames,
	CutscenesAudioNames,
	FX1AudioNames,
	UIAudioNames,
} from "@/game/entities/config/audio/audioConfig";
import type { ResourceType } from "@/game/entities/config/resources";
import type { Chest, ChestReward } from "@/game/entities/objects/chests";
import type TileEntity from "@/game/entities/TileEntity";
import type { TileToastType, ToastPlacement } from "@/game/ToastMessage";
import { type MapExpansionKey } from "@/store/useMapExpansionStore";
import type {
	TileCoordinatesWithValue,
	TileIndex,
	TileLabelSkin,
	TileLabelType,
} from "@/types/tile";
import {
	type TileCoordinates,
	type TileEntityOnClickMethodReturnType,
} from "@/types/tile";
import type { OfferType } from "@/types/ui";
import mitt from "mitt";

export type ConnectionErrorOptions = { recoverable?: boolean };

// TODO: TileCoordinates & { frameName: FrameName | null }
export type TileRepositoryUpgradePayload = {
	coordinates: TileCoordinates;
	frameName: FrameName | null;
	noRefresh?: boolean;
	onlyPalisades?: boolean;
};

export type PhaserPutTileAtPayload = {
	// TODO: nemusi to byt objekt ale rovno tie koordinaty
	coordinates: TileCoordinates;
	fade?: boolean;
};

export type PlagueProgressUpdatePayload = TileCoordinates & {
	currentHp: number;
	maxHp: number;
	heal: boolean;
};

export type PhaserTileFogAddedPayload = TileCoordinates & {
	fade?: boolean;
};

export type TileRepositoryAddToRoadGraphPayload = {
	tileIndex: TileIndex;
};

export type PhaserTileClickPayload = TileCoordinates & {
	reward: TileEntityOnClickMethodReturnType["reward"] | ChestReward;
	messageTop?: number;
	messageScale?: boolean;
	messageNoAnim?: boolean;
	placement?: ToastPlacement.TOP_CENTER;
	randomX?: boolean;
	isSpecialChest?: boolean;
	messageNumberOffset?: number;
};

export type PhaserTileClickResource = TileCoordinates & {
	resource: string;
};

export type PhaserTileClickText = TileCoordinates & {
	text: string;
	topCorrection?: number;
	messageNoAnim?: boolean;
	messageTime?: number;
	replace?: { placeholder: string; value: string };
	messageNumber?: number;
	ignoreBounds?: boolean;
};

export type PhaserMessageText = {
	text: string;
	type: TileToastType;
	placement?: ToastPlacement;
};

export type PhaserTileUpdateProgressBarPayload = TileCoordinates;

export type PhaserRemoveProgressBarPayload = TileCoordinates;

export type MapChangePayload = { tileIndex: TileIndex };

export interface AudioEmitterData {
	spriteName: AudioSpriteNames;
	audioName?: UIAudioNames | CutscenesAudioNames | FX1AudioNames;
	options?: { loop?: boolean };
}

export type PhaserDisconnectedTilesPayload = Set<TileCoordinatesWithValue>;
export type PhaserBlockedByEnemyPayload = Set<TileCoordinatesWithValue>;

export type PhaserUpdateTileLabelPayload = {
	coordinates: TileCoordinates;
	skin: TileLabelSkin;
	action: TileLabelAction;
	type?: TileLabelType;
	scaleToZoom?: boolean;
};

export type PhaserRevealRangePayload = TileCoordinates & {
	range: number;
};

export type PhaserUnlockRegionPayload = {
	id: MapExpansionKey;
};

export type PhaserTileLabelClickPayload = {
	tile: TileEntity | null;
	callback?: () => void;
};

export enum TileLabelAction {
	CREATE = "create",
	DELETE = "delete",
	DELETE_ALL = "delete_all",
	DELETE_MULTIPLE_SKINS_AT_COORS = "delete_multiple_skins_at_coors",
	UPDATE = "update",
}

export type IncomeReward = {
	reward: Partial<Record<ResourceType, number>>;
};

export type SiegeSystemDamagePayload = number | null;

export type SiegeSystemProgressUpdatePayload = TileCoordinates;

// TODO: Treba pretriedit emity, napr. PhaserRedirect vobec nepotrebujeme...
export enum EmitterEvents {
	PhaserPutTileAt = "Phaser/PutTileAt", // TODO: rename
	PhaserTileClick = "Phaser/SettlementClick",
	PhaserSettlementCastleClick = "Phaser/LevelBadgeClick",
	PhaserTileUpdateProgressBar = "Phaser/UpdateProgressBar",
	PhaserRemoveProgressBar = "Phaser/SettlementRemoveProgressBar",
	PhaserTutorialSettlementRevealed = "Phaser/PhaserTutorialSettlementRevealed",
	PhaserCreateGemsBadge = "Phaser/ShowGemsBadge",
	PhaserDestroyGemsBadge = "Phaser/HideGemsBadge",
	PhaserRevealRange = "Phaser/RevealRange",
	PhaserUnlockRegion = "Phaser/RevealRegion",
	TileRepositoryUpgrade = "TileRepo/UpgradeEntity",
	TileRepositoryAddToRoadGraph = "TileRepo/AddToRoadGraph",
	GameInitStoryMode = "Game/InitStoryMode",
	GameShowOfflinePopup = "Game/ShowOfflinePopup",
	GameRecalculateOfflineRewards = "Game/RecalculateOfflineRewards",
	GameShowInactivePopup = "Game/ShowInactivePopup",
	GameShowConnectionErrorPopup = "Game/ShowConnectionErrorPopup",
	GameTick = "Game/Tick",
	MapChange = "Map/Change",
	TutorialStart = "Tutorial/Start",
	TutorialEnd = "Tutorial/End",
	GameLoadingDone = "Game/LoadingDone",
	TranslationsChanged = "Translations/Changed",
	SettlementPropsUpdate = "Game/SettlementPropsUpdate",
	PhaserLoadAudio = "Phaser/LoadAudio",
	PhaserLoadEventMapAssets = "Phaser/LoadEventMapAssets",

	// Visuals
	PingSpecificTile = "Ping/SpecificTile",
	UnpingSpecificTile = "Unping/SpecificTile",
	ToggleLevelUpPopup = "Toggle/LevelUpPopup",
	SwitchPopupTab = "SwitchPopupTab",
	EventRecalc = "Event/Recalc",
	SiegeSystemDamage = "SiegeSystem/Damage",
	SiegeSystemProgressUpdate = "SiegeSystem/ProgressUpdate",

	// Interactions
	PhaserTileDetailOpen = "Phaser/TileDetailOpen",
	PhaserTileSelect = "Phaser/TileSelect",
	PhaserTileDeselect = "Phaser/TileDeselect",
	PhaserRedirect = "Phaser/Redirect",
	PhaserSceneReady = "Phaser/SceneReady",
	PhaserRecalculateExplorationSpeed = "Phaser/RecalculateExplorationSpeed",
	PhaserActiveDebug = "Phaser/PhaserActiveDebug",
	PhaserPopupOpen = "Phaser/PhaserPopupOpen",
	PhaserTutorialTileUpgrade = "Phaser/PhaserTutorialTileUpgrade",
	PhaserRenderDisconnect = "Phaser/PhaserRenderDisconnect",
	PhaserTileExplored = "Phaser/TileExplored",
	PhaserAddRangeSelector = "Phaser/AddRangeSelector",
	PhaserRemoveRangeSelector = "Phaser/RemoveRangeSelector",
	PhaserAddChestToMap = "Phaser/AddChestToMap",
	PhaserAddDarkChestToMap = "Phaser/AddDarkChestToMap",
	PhaserRemoveChestFromMap = "Phaser/RemoveChestFromMap",
	PhaserTileFogAdded = "Phaser/TileFogAdded",
	PhaserRenderBlockedByEnemy = "Phaser/RenderBlockedByEnemy",
	PhaserNotEnoughResource = "Phaser/NotEnoughResource",
	PhaserWarningMessage = "Phaser/NotWarningMessage ",
	PhaserSuccessMessage = "Phaser/SuccessMessage",
	PhaserNeutralMessage = "Phaser/NeutralMessage",
	PhaserPlayAudio = "Phaser/PlayAudio",
	PhaserStopAudio = "Phaser/StopAudio",
	PhaserPlayMainAudio = "Phaser/PlayMainAudio",
	PhaserPlayCityAmbient = "Phaser/PlayCityAmbient",
	PhaserAudioFinished = "Phaser/AudioFinished",
	PhaserStopMainAudio = "Phaser/StopMainAudio",
	PhaserStopCityAmbient = "Phaser/StopCityAmbient",
	PhaserChangeMainMusicVolume = "Phaser/ChangeMainMusicVolume",
	PhaserPlayIntroAudio = "Phaser/PlayIntroAudio",
	PhaserStopIntroAudio = "Phaser/StopIntroAudio",
	PhaserTextMessage = "Phaser/TextMessage",
	PhaserAnimateIntroCamera = "Phaser/AnimateIntroCamera",
	PhaserShowCameraResetButton = "Phaser/ShowCameraResetButton",
	PhaserHideCameraResetButton = "Phaser/HideCameraResetButton",
	PhaserGoToCapital = "Phaser/GoToCapital",
	PhaserMakeScreenshot = "Phaser/MakeScreenshot",
	PhaserUpdateTileLabel = "Phaser/UpdateTileLabel",
	PhaserTileLabelClick = "Phaser/TileLabelClick",
	LanguageChanged = "Language/Changed",
	PhaserBuildAnimation = "Phaser/BuildAnimation",
	PhaserDemolishAnimation = "Phaser/DemolishAnimation",
	PhaserUpgradeAnimation = "Phaser/UpgradeAnimation",
	PhaserSettlementAnimation = "Phaser/SettlementAnimation",
	NewSpecialLocation = "NewSpecialLocation",
	SettlementQuestUpdate = "SettlementQuestUpdate",
	OfflineIncome = "OfflineIncome",
	ShopItemPurchased = "Shop/ItemPurchased",
	EventLocalStorageUpdated = "Event/LocalStorageUpdated",
	SoundVolumeUpdated = "Sound/VolumeUpdated",
	PhaserHighlightSettlements = "Phaser/HighlightSettlements",
	PlagueProgressUpdate = "Plague/ProgressUpdate",
	PhaserToggleDragging = "PhaserToggleDragging",
	PhaserSettlementSwitch = "Phaser/SettlementSwitch",
	PhaserEventiumIncome = "Phaser/EventiumIncome",
	/**
	 * Emitted to open the referral program tab in SettingsView.
	 * Used by the `referral` deep link handler.
	 * TODO: Wire up SettingsView to listen for this event and
	 * auto-scroll/focus the referral tab once the feature is implemented.
	 */
	OpenReferralTab = "Game/OpenReferralTab",
}

const emitter = mitt<{
	[EmitterEvents.PhaserPutTileAt]: PhaserPutTileAtPayload;
	[EmitterEvents.PhaserTileClick]: PhaserTileClickPayload;
	[EmitterEvents.PhaserSettlementCastleClick]: TileCoordinates;
	[EmitterEvents.PhaserTileUpdateProgressBar]: PhaserTileUpdateProgressBarPayload;
	[EmitterEvents.PhaserRemoveProgressBar]: PhaserRemoveProgressBarPayload;
	[EmitterEvents.PhaserCreateGemsBadge]: void;
	[EmitterEvents.PhaserDestroyGemsBadge]: TileCoordinates;
	[EmitterEvents.PhaserRevealRange]: PhaserRevealRangePayload;
	[EmitterEvents.PhaserUnlockRegion]: PhaserUnlockRegionPayload;
	[EmitterEvents.TileRepositoryUpgrade]: TileRepositoryUpgradePayload;
	[EmitterEvents.TileRepositoryAddToRoadGraph]: TileRepositoryAddToRoadGraphPayload;
	[EmitterEvents.GameInitStoryMode]: void;
	[EmitterEvents.GameShowOfflinePopup]: boolean;
	[EmitterEvents.GameRecalculateOfflineRewards]: void;
	[EmitterEvents.GameShowInactivePopup]: void;
	[EmitterEvents.GameShowConnectionErrorPopup]: ConnectionErrorOptions | void;
	[EmitterEvents.GameTick]: void;
	[EmitterEvents.MapChange]: MapChangePayload;
	[EmitterEvents.GameLoadingDone]: void;
	[EmitterEvents.PhaserLoadAudio]: void;
	[EmitterEvents.PhaserLoadEventMapAssets]: void;

	[EmitterEvents.PingSpecificTile]: TileCoordinates;
	[EmitterEvents.UnpingSpecificTile]: TileCoordinates;
	[EmitterEvents.ToggleLevelUpPopup]: boolean;
	[EmitterEvents.SwitchPopupTab]: void;
	[EmitterEvents.EventRecalc]: void;
	[EmitterEvents.SiegeSystemDamage]: SiegeSystemDamagePayload;
	[EmitterEvents.SiegeSystemProgressUpdate]: SiegeSystemProgressUpdatePayload;

	[EmitterEvents.PhaserTileDetailOpen]: TileCoordinates;
	[EmitterEvents.PhaserTileDeselect]: void;
	[EmitterEvents.PhaserTileSelect]: TileCoordinates;
	[EmitterEvents.PhaserTileExplored]: TileCoordinates;
	[EmitterEvents.PhaserRedirect]: void;
	[EmitterEvents.PhaserSceneReady]: void;
	[EmitterEvents.PhaserRecalculateExplorationSpeed]: void;
	[EmitterEvents.PhaserActiveDebug]: void;
	[EmitterEvents.PhaserTutorialSettlementRevealed]: void;
	[EmitterEvents.PhaserTutorialTileUpgrade]: TileRepositoryUpgradePayload;
	[EmitterEvents.PhaserRenderDisconnect]: PhaserDisconnectedTilesPayload;
	[EmitterEvents.TutorialStart]: void;
	[EmitterEvents.TutorialEnd]: void;
	[EmitterEvents.PhaserAddRangeSelector]: TileCoordinates;
	[EmitterEvents.PhaserRemoveRangeSelector]: TileCoordinates;
	[EmitterEvents.PhaserAddChestToMap]: Chest;
	[EmitterEvents.PhaserAddDarkChestToMap]: Chest;
	[EmitterEvents.PhaserRemoveChestFromMap]: Chest;
	[EmitterEvents.PhaserTileFogAdded]: PhaserTileFogAddedPayload;
	[EmitterEvents.PhaserRenderBlockedByEnemy]: PhaserBlockedByEnemyPayload;
	[EmitterEvents.PhaserNotEnoughResource]: PhaserTileClickResource;
	[EmitterEvents.PhaserWarningMessage]: PhaserTileClickText;
	[EmitterEvents.PhaserSuccessMessage]: PhaserTileClickText;
	[EmitterEvents.PhaserNeutralMessage]: PhaserTileClickText;
	[EmitterEvents.PhaserPlayAudio]: AudioEmitterData;
	[EmitterEvents.PhaserStopAudio]: AudioEmitterData;
	[EmitterEvents.PhaserPlayMainAudio]: void;
	[EmitterEvents.PhaserPlayCityAmbient]: void;
	[EmitterEvents.PhaserAudioFinished]:
		| UIAudioNames
		| CutscenesAudioNames
		| FX1AudioNames;
	[EmitterEvents.PhaserStopMainAudio]: void;
	[EmitterEvents.PhaserStopCityAmbient]: void;
	[EmitterEvents.PhaserChangeMainMusicVolume]: void;
	[EmitterEvents.PhaserPlayIntroAudio]: void;
	[EmitterEvents.PhaserStopIntroAudio]: void;
	[EmitterEvents.PhaserTextMessage]: PhaserMessageText;
	[EmitterEvents.PhaserAnimateIntroCamera]: void;
	[EmitterEvents.PhaserShowCameraResetButton]: void;
	[EmitterEvents.PhaserHideCameraResetButton]: void;
	[EmitterEvents.PhaserGoToCapital]: void;
	[EmitterEvents.PhaserMakeScreenshot]: void;
	[EmitterEvents.PhaserUpdateTileLabel]: PhaserUpdateTileLabelPayload;
	[EmitterEvents.PhaserTileLabelClick]: PhaserTileLabelClickPayload;
	[EmitterEvents.TranslationsChanged]: void;
	[EmitterEvents.SettlementPropsUpdate]: void;

	[EmitterEvents.LanguageChanged]: void;
	[EmitterEvents.PhaserBuildAnimation]: TileCoordinates;
	[EmitterEvents.PhaserDemolishAnimation]: TileCoordinates;
	[EmitterEvents.PhaserUpgradeAnimation]: TileCoordinates;
	[EmitterEvents.PhaserSettlementAnimation]: TileCoordinatesWithValue;
	[EmitterEvents.NewSpecialLocation]: void;
	[EmitterEvents.SettlementQuestUpdate]: void;
	[EmitterEvents.OfflineIncome]: IncomeReward;
	[EmitterEvents.ShopItemPurchased]: {
		offerId: number;
		offerType: OfferType;
	};
	[EmitterEvents.EventLocalStorageUpdated]: void;
	[EmitterEvents.SoundVolumeUpdated]: void;
	[EmitterEvents.PhaserHighlightSettlements]: void;
	[EmitterEvents.PlagueProgressUpdate]: PlagueProgressUpdatePayload;
	[EmitterEvents.PhaserToggleDragging]: boolean;
	[EmitterEvents.PhaserSettlementSwitch]: TileEntity;
	[EmitterEvents.PhaserEventiumIncome]: PhaserTileClickPayload;
	[EmitterEvents.OpenReferralTab]: void;
}>();

export default (): typeof emitter => emitter;
